import { ACTIVITY_TYPES_CORE } from '@federa/const/observability-activity-types-core';
import { ACTIVITY_TYPES_KAIZEN } from '@federa/const/observability-activity-types-kaizen';
import { ACTIVITY_TYPES_UI } from '@federa/const/observability-activity-types-ui';

/**
 * Activity field pairs that may arrive in either casing; each entry maps the snake_case canonical key to its camelCase
 * alias. READ-SIDE ONLY: no client writer produces these pairs (there is no client activity-tracking emitter). The live
 * consumers are the server-side reconcilers -- `getFieldPair` /
 * `normalizeDualKeyField` in `tools/activity/event-schema-payload.ts` and the field loop in
 * `flows/user-activity-save.ts` -- which still collapse dual-cased payloads produced by OTHER producers.
 *
 * NAME THOSE PRODUCERS BEFORE PROPOSING A RETIREMENT, because "no client writer remains" is true and is NOT the
 * question. The `user-activity-save` reconciler serves the UserDO WEBSOCKET path, whose activity bag is built from a
 * client-framed message: `routeUserWebSocketMessage` parses the socket frame into `data`, `addSearchActivity` hands
 * that same bag to `_addActivity`, and `addActivity` SPREADS it into the activity record -- so any camelCase key a
 * socket client frames lands on the record verbatim. The same `data` also rides in as `requestData`, which is why
 * `applyActivityIdentityContext` / `applyActivityStatusAndDuration` (`utils/activity/context-appliers.ts`) read both
 * spellings too. Those appliers only WRITE the snake key; `normalizeDualKeyField` is what DELETES the camel twin, and
 * without that delete the twin survives into the row's `payload` JSON beside its own canonical column.
 */
export const ACTIVITY_FIELD_PAIRS = [
	{ snake: 'user_id', camel: 'userId' },
	{ snake: 'session_id', camel: 'sessionId' },
	{ snake: 'property_id', camel: 'propertyId' },
	{ snake: 'page_url', camel: 'pageUrl' },
	{ snake: 'success_status', camel: 'successStatus' },
	{ snake: 'duration_ms', camel: 'durationMs' },
] as const;

/**
 * @runtime-derived-ssot-helper (const-leaf-pinned): the DUAL-CASED alias tuple for ONE canonical activity field,
 * derived from {@link ACTIVITY_FIELD_PAIRS} instead of respelled.
 *
 * WHY IT EXISTS. `EventDefinition.required` / `.recommended` (`types/api-activity.ts`) admit a nested array as "any
 * ONE of these spellings satisfies the requirement", and the `const/activity-event-catalog-*` data leaves spelled
 * that tuple out ten times -- nine `['property_id', 'propertyId']` and one `['page_url', 'pageUrl']` -- beside the
 * pair list above that already owns exactly the same fact. A seventh casing pair, or a change to an existing one,
 * had to be made in eleven places or the catalog and the reconcilers would disagree about which key an emitter may
 * send. The parameter is typed to the DECLARED snake keys, so a field with no pair is a compile error at the
 * catalog rather than a runtime miss.
 *
 * A FRESH ARRAY PER CALL, not a shared lookup record: `required` is a MUTABLE `Array<string | string[]>`, so one
 * shared instance would be aliased across every catalog entry naming the same field.
 */
export function activityFieldAliases(snake: (typeof ACTIVITY_FIELD_PAIRS)[number]['snake']): string[] {
	const pair = ACTIVITY_FIELD_PAIRS.find((entry) => entry.snake === snake);
	if (!pair) throw new Error(`No activity field pair declares the snake key "${snake}" -- declared: ${ACTIVITY_FIELD_PAIRS.map((entry) => entry.snake).join(', ')}.`);
	return [pair.snake, pair.camel];
}

export const ACTIVITY_TYPES = {
	...ACTIVITY_TYPES_CORE,
	...ACTIVITY_TYPES_UI,
	...ACTIVITY_TYPES_KAIZEN,
} as const;

export const EXCLUDED_ACTIVITY_TYPES = [
	ACTIVITY_TYPES.WEBSOCKET_DISCONNECTED,
	ACTIVITY_TYPES.AUTHENTICATION_ATTEMPT,
	ACTIVITY_TYPES.SESSION_DB_OPERATION,
] as const;

/** Trigger identifiers passed to queueActivity / executeBalanceAdjustment */
export const ACTIVITY_TRIGGERS = {
	BALANCE_ADJUSTMENT: 'balance_adjustment',
	RPC_BALANCE_ADJUSTMENT: 'rpc_balance_adjustment',
	WEBSOCKET_AUTH: 'websocket_auth',
	WEBSOCKET_CLOSE: 'websocket_close',
	WEBSOCKET_WISHLIST: 'websocket_wishlist',
	ACTIVITY_ADAPTER: 'activity_adapter',
	HANDLE_BATCH: 'handleBatch',
	HANDLE_OFFERS_FORM: 'handleOffersForm',
	MODAL: 'modal',
	API: 'api',
	WEBHOOK: 'webhook',
	CLI: 'cli',
	// Session-builder (DO login/logout activity object) default triggers.
	LOGIN_SUCCESS: 'login_success',
	LOGOUT_ACTION: 'logout_action',
	// Event-catalog default triggers. LOGIN_COMPLETE is intentionally distinct from LOGIN_SUCCESS above -- the DO session-builder pipeline and the event-catalog pipeline are separate producers for the same conceptual event.
	LOGIN_COMPLETE: 'login_complete',
	REGISTRATION_COMPLETE: 'registration_complete',
	PROFILE_EDIT: 'profile_edit',
	PAGE_NAVIGATION: 'page_navigation',
	NAVIGATION_ACTION: 'navigation_action',
	/** The once-per-mount PAGE_VIEW the browser telemetry provider emits (`useNavigationCapture`,
	 *  `apps/federa/src/contexts/TelemetryProvider.capture-effects.ts`). DISTINCT from PAGE_NAVIGATION, which is the
	 *  event-catalog default for a NAVIGATION between routes: this one fires exactly once, when the SPA is first
	 *  mounted, and no per-route PAGE_VIEW is emitted anywhere. It is named here rather than left a bare literal at that
	 *  emitter, because it is the one trigger a reader of this record would most expect to find. */
	SESSION_LANDING: 'session_landing',
} as const;

/** Source identifier for operations triggered via WebSocket connections */
export const ACTIVITY_SOURCE_WEBSOCKET = 'websocket' as const;
